import { useEffect, useState } from "react";
import { UserCog, Plus, Pencil, Trash2, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const ROLES = [
  { key: "super_admin", label: "Super Admin", desc: "Akses penuh ke semua fitur & semua bisnis" },
  { key: "business_admin", label: "Admin Bisnis", desc: "Dashboard, leads, konten, template & ide untuk bisnis yang ditugaskan" },
  { key: "operator", label: "Operator", desc: "Dashboard, generate konten & lihat jadwal posting" },
];
const ROLE_META = {
  super_admin: { label: "Super Admin", cls: "bg-[#10B981]/15 text-[#059669]" },
  business_admin: { label: "Admin Bisnis", cls: "bg-[#3B82F6]/15 text-[#2563EB]" },
  operator: { label: "Operator", cls: "bg-[#F59E0B]/15 text-[#B45309]" },
};

const EMPTY = { full_name: "", username: "", password: "", email: "", role: "operator", business_ids: [] };

const DOT_COLORS = ["#10B981", "#3B82F6", "#F59E0B", "#F43F5E", "#A855F7", "#2563EB", "#14B8A6", "#EC4899"];

export default function UserManagement({ businesses, currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setUsers((await api.get("/users")).data || []); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => {
    setEditId(u.id);
    setForm({ full_name: u.full_name || "", username: u.username, password: "", email: u.email || "", role: u.role, business_ids: u.business_ids || [] });
    setOpen(true);
  };

  const toggleBiz = (id) => setForm((f) => ({
    ...f,
    business_ids: f.business_ids.includes(id) ? f.business_ids.filter((x) => x !== id) : [...f.business_ids, id],
  }));

  const save = async () => {
    if (!form.username.trim()) { toast.error("Username wajib diisi"); return; }
    if (!editId && !form.password) { toast.error("Password wajib diisi"); return; }
    setSaving(true);
    try {
      if (editId) {
        const payload = { full_name: form.full_name, username: form.username, email: form.email, role: form.role, business_ids: form.business_ids };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editId}`, payload);
        toast.success("User diperbarui");
      } else {
        await api.post("/users", form);
        toast.success("User baru ditambahkan");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { active: !u.active });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Hapus user "${u.username}"? Tindakan ini tidak bisa dibatalkan.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User dihapus");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const bizNames = (ids) => {
    if (!ids?.length) return "—";
    return ids.map((id) => businesses.find((b) => b.id === id)?.name || "?").join(", ");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <UserCog size={18} className="text-[#2563EB]" />
            <h2 className="font-heading font-bold">Kelola User</h2>
          </div>
          <p className="text-xs text-slate-500">Tambah, atur hak akses, dan kelola pengguna aplikasi.</p>
        </div>
        <Button onClick={openNew} data-testid="add-user-button" className="bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl">
          <Plus size={16} className="mr-1" /> Tambah User
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#2563EB]" /></div>
      ) : (
        <div className="bg-[#FFFFFF] border border-[#E2E8F0] rounded-2xl min-w-0 overflow-x-auto" data-testid="users-table">
          <table className="w-full min-w-[680px] text-sm">
            <thead className="bg-[#F1F5F9] text-slate-600 text-xs">
              <tr>
                <th className="text-left px-5 py-3 font-semibold">Nama / Username</th>
                <th className="text-left px-5 py-3 font-semibold">Role</th>
                <th className="text-left px-5 py-3 font-semibold">Bisnis</th>
                <th className="text-left px-5 py-3 font-semibold">Status</th>
                <th className="text-right px-5 py-3 font-semibold">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-t border-[#E2E8F0]">
                  <td className="px-5 py-3">
                    <p className="font-medium text-slate-800">{u.full_name || "—"}</p>
                    <p className="text-xs text-slate-500">@{u.username}{u.email ? ` · ${u.email}` : ""}</p>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded ${ROLE_META[u.role]?.cls}`}>{ROLE_META[u.role]?.label}</span>
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-600 max-w-[220px] truncate">{u.role === "super_admin" ? "Semua bisnis" : bizNames(u.business_ids)}</td>
                  <td className="px-5 py-3">
                    <button onClick={() => toggleActive(u)} disabled={u.id === currentUser?.id} data-testid={`toggle-active-${u.id}`} className={`text-[11px] font-semibold px-2 py-0.5 rounded ${u.active ? "bg-[#10B981]/15 text-[#059669]" : "bg-slate-600/20 text-slate-600"} disabled:opacity-50`}>
                      {u.active ? "Aktif" : "Nonaktif"}
                    </button>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button onClick={() => openEdit(u)} data-testid={`edit-user-${u.id}`} className="p-1.5 rounded-lg hover:bg-[#F1F5F9] text-slate-600 hover:text-[#2563EB] transition-colors"><Pencil size={15} /></button>
                      <button onClick={() => remove(u)} disabled={u.id === currentUser?.id} data-testid={`delete-user-${u.id}`} className="p-1.5 rounded-lg hover:bg-[#F1F5F9] text-slate-600 hover:text-[#E11D48] transition-colors disabled:opacity-30"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-md max-h-[min(90vh,calc(100dvh-2rem))] overflow-y-auto" data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editId ? "Edit User" : "Tambah User Baru"}</DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">Atur data dan hak akses pengguna.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-slate-600">Nama Lengkap</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="user-fullname-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" placeholder="Budi Santoso" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-600">Username</Label>
                <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="user-username-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" placeholder="username" />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" placeholder="email@contoh.com" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Password {editId && <span className="text-slate-600">(opsional)</span>}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1" placeholder={editId ? "••••• (biarkan kosong untuk tidak mengubah)" : "min. 4 karakter"} />
            </div>
            <div>
              <Label className="text-xs text-slate-600">Hak Akses (Role)</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select" className="bg-[#F1F5F9] border-[#E2E8F0] mt-1"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
                  {ROLES.map((r) => (
                    <SelectItem key={r.key} value={r.key} className="focus:bg-[#F1F5F9]">{r.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">{ROLES.find((r) => r.key === form.role)?.desc}</p>
            </div>
            {form.role !== "super_admin" && (
              <div data-testid="user-business-picker">
                <Label className="text-xs text-slate-600">Bisnis yang Dapat Diakses</Label>
                <div className="mt-2 space-y-0.5 max-h-52 overflow-y-auto border border-[#E2E8F0] rounded-xl p-2">
                  {businesses.length === 0 && <p className="text-xs text-slate-500 px-1 py-2">Belum ada bisnis.</p>}
                  {businesses.map((b, i) => {
                    const checked = form.business_ids.includes(b.id);
                    const color = DOT_COLORS[i % DOT_COLORS.length];
                    return (
                      <button
                        type="button"
                        key={b.id}
                        onClick={() => toggleBiz(b.id)}
                        aria-pressed={checked}
                        data-testid={`user-biz-${b.id}`}
                        className="flex items-center gap-3 w-full px-2.5 py-2 rounded-lg hover:bg-[#F1F5F9] transition-colors text-left"
                      >
                        <span
                          className="flex items-center justify-center w-[18px] h-[18px] rounded-full border-2 shrink-0 transition-colors"
                          style={{ borderColor: checked ? color : "#334155", backgroundColor: checked ? color : "transparent" }}
                        >
                          {checked && <Check size={12} className="text-[#F8FAFC]" strokeWidth={3} />}
                        </span>
                        <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                        <span className="text-sm text-slate-800">{b.name}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="text-[11px] text-slate-500 mt-1.5">{form.business_ids.length} bisnis dipilih. Klik untuk memilih lebih dari satu.</p>
              </div>
            )}
            <Button onClick={save} disabled={saving} data-testid="save-user-button" className="w-full bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl">
              {saving ? <Loader2 size={16} className="animate-spin mr-1" /> : null} {editId ? "Simpan Perubahan" : "Tambah User"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
